class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.debouncedOnSubmit = debounce((event) => this.onSubmitHandler(event), 250);

    const facetForm = this.querySelector('form');
    if (facetForm) facetForm.addEventListener('input', this.debouncedOnSubmit);

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    window.addEventListener('popstate', (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    });
  }

  static setLoading(isLoading) {
    const grid = document.getElementById('ProductGridContainer');
    grid?.classList.toggle('loading', isLoading);
    if (grid) {
      if (isLoading) grid.setAttribute('aria-busy', 'true');
      else grid.removeAttribute('aria-busy');
    }

    ['ProductCount', 'ProductCountDesktop'].forEach((id) => {
      document.getElementById(id)?.classList.toggle('loading', isLoading);
    });

    document
      .querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
      .forEach((spinner) => spinner.classList.toggle('hidden', !isLoading));
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
      element.toggleAttribute('aria-disabled', disable);
    });
  }

  static getSectionId() {
    return document.getElementById('product-grid')?.dataset.id || document.getElementById('main-collection-filters')?.dataset.id;
  }

  static buildUrl(searchParams) {
    const sectionId = FacetFiltersForm.getSectionId();
    if (!sectionId) return null;
    const params = new URLSearchParams(searchParams || '');
    params.set('section_id', sectionId);
    return `${window.location.pathname}?${params.toString()}`;
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    const url = FacetFiltersForm.buildUrl(searchParams);
    if (!url) {
      console.error('[LED Truck Shop] Filter section id missing.');
      return;
    }

    FacetFiltersForm.searchParamsPrev = searchParams;
    FacetFiltersForm.setLoading(true);
    FacetFiltersForm.toggleActiveFacets(true);

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);

    const cached = FacetFiltersForm.filterData.get(url);
    if (cached) {
      FacetFiltersForm.renderSection(cached, event);
      return;
    }

    FacetFiltersForm.controller?.abort();
    FacetFiltersForm.controller = new AbortController();

    fetch(url, {
      signal: FacetFiltersForm.controller.signal,
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Filter request failed: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        FacetFiltersForm.filterData.set(url, html);
        if (FacetFiltersForm.filterData.size > 12) {
          FacetFiltersForm.filterData.delete(FacetFiltersForm.filterData.keys().next().value);
        }
        FacetFiltersForm.renderSection(html, event);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error('[LED Truck Shop] Filter update failed', error);
        FacetFiltersForm.setLoading(false);
        FacetFiltersForm.toggleActiveFacets(false);
      });
  }

  static renderSection(html, event) {
    try {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const sourceGrid = parsed.getElementById('ProductGridContainer');
      const targetGrid = document.getElementById('ProductGridContainer');

      if (!sourceGrid || !targetGrid) {
        throw new Error('Filtered section response does not contain ProductGridContainer');
      }

      FacetFiltersForm.renderForms(parsed, event);
      targetGrid.innerHTML = sourceGrid.innerHTML;
      targetGrid.querySelectorAll('.scroll-trigger').forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

      FacetFiltersForm.renderProductCount(parsed);
      FacetFiltersForm.setLoading(false);
      FacetFiltersForm.toggleActiveFacets(false);

      document.dispatchEvent(new CustomEvent('lts:collection-updated', {
        detail: { searchParams: FacetFiltersForm.searchParamsPrev },
      }));

      if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
    } catch (error) {
      console.error('[LED Truck Shop] Filter render failed', error);
      FacetFiltersForm.setLoading(false);
      FacetFiltersForm.toggleActiveFacets(false);
    }
  }

  static renderForms(parsed, event) {
    const openFilterId = event?.target?.closest?.('.js-filter')?.id || null;
    const inputId = event?.target?.id || null;

    ['FacetFiltersForm', 'FacetFiltersFormMobile', 'FacetFiltersPillsForm'].forEach((id) => {
      const source = parsed.getElementById(id);
      const target = document.getElementById(id);
      if (source && target) target.innerHTML = source.innerHTML;
    });

    ['.active-facets-mobile', '.active-facets-desktop'].forEach((selector) => {
      const sources = parsed.querySelectorAll(selector);
      const targets = document.querySelectorAll(selector);
      sources.forEach((source, index) => {
        if (targets[index]) targets[index].innerHTML = source.innerHTML;
      });
    });

    if (openFilterId) {
      const reopened = document.getElementById(openFilterId);
      if (reopened?.tagName === 'DETAILS') reopened.open = true;
      const focusTarget = inputId ? document.getElementById(inputId) : null;
      if (focusTarget && !window.matchMedia('(pointer: coarse)').matches) {
        requestAnimationFrame(() => focusTarget.focus({ preventScroll: true }));
      }
    }

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    const mobileDrawer = mobileForm?.closest('menu-drawer');
    if (mobileDrawer && typeof mobileDrawer.bindEvents === 'function') mobileDrawer.bindEvents();
  }

  static renderProductCount(parsed) {
    const sourceCount = parsed.getElementById('ProductCount');
    const sourceDesktop = parsed.getElementById('ProductCountDesktop');
    const mobile = document.getElementById('ProductCount');
    const desktop = document.getElementById('ProductCountDesktop');

    if (sourceCount && mobile) {
      mobile.innerHTML = sourceCount.innerHTML;
      if (sourceCount.dataset.productCount) mobile.dataset.productCount = sourceCount.dataset.productCount;
    }
    if (desktop) {
      desktop.innerHTML = sourceDesktop?.innerHTML || sourceCount?.innerHTML || desktop.innerHTML;
    }
  }

  static updateURLHash(searchParams) {
    const next = `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`;
    history.pushState({ searchParams }, '', next);
  }

  createSearchParams(form) {
    return new URLSearchParams(new FormData(form)).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const form = event.target.closest('form');
    if (!form) return;

    const isMobile = form.id === 'FacetFiltersFormMobile';
    if (isMobile) {
      this.onSubmitForm(this.createSearchParams(form), event);
      return;
    }

    const params = [];
    document.querySelectorAll('facet-filters-form form').forEach((candidate) => {
      if (['FacetSortForm', 'FacetFiltersForm', 'FacetSortDrawerForm'].includes(candidate.id)) {
        const value = this.createSearchParams(candidate);
        if (value) params.push(value);
      }
    });
    this.onSubmitForm(params.join('&'), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const href = event.currentTarget.href;
    const params = href.includes('?') ? href.slice(href.indexOf('?') + 1) : '';
    FacetFiltersForm.renderPage(params);
  }
}

FacetFiltersForm.filterData = new Map();
FacetFiltersForm.controller = null;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
if (!customElements.get('facet-filters-form')) customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
